import {
    ReactNode,
    createContext,
    useContext,
    useEffect,
    useState,
} from "react";
import { useLocation } from "react-router";
import moment from "moment-timezone";

// IMPORTANT NOTE: DST rule changes often for some time zones (e.g. Africa/Cairo), so it's important to update the moment-timezone version frequently.

const defaultTimeZone = moment.tz.guess(); // getting a browser time zone
const TimeZoneContext = createContext<string>(defaultTimeZone);
const TIME_ZONE_QUERY_PARAM_NAME = "timezone";
const supportedTimeZoneList = moment.tz.names();

function isTimeZoneValid(timeZoneId: string) {
    // Note that this is a rough validation.
    // The time zone values we can select in gux-timezone-picker (Workspace level) are subset of the values stored in supportedTimeZoneList.
    // We tried using api/v2/timezones value to validate the time zone id, but it did not work very well because some values don't exist in the response.
    return supportedTimeZoneList.includes(timeZoneId);
}

export function TimeZoneProvider({ children }: { children: ReactNode }) {
    const location = useLocation();

    // Initialize timezone synchronously from query param to avoid race conditions
    // This ensures the correct timezone is available on first render
    const getInitialTimeZone = () => {
        const timeZoneValueFromQueryParam = new URLSearchParams(
            location.search
        ).get(TIME_ZONE_QUERY_PARAM_NAME);
        if (
            timeZoneValueFromQueryParam &&
            isTimeZoneValid(timeZoneValueFromQueryParam)
        ) {
            return timeZoneValueFromQueryParam;
        }
        return defaultTimeZone;
    };

    const [timeZone, setTimeZone] = useState<string>(getInitialTimeZone);

    useEffect(() => {
        const timeZoneValueFromQueryParam = new URLSearchParams(
            location.search
        ).get(TIME_ZONE_QUERY_PARAM_NAME);
        if (
            timeZoneValueFromQueryParam &&
            isTimeZoneValid(timeZoneValueFromQueryParam)
        ) {
            setTimeZone(timeZoneValueFromQueryParam);
        } else {
            setTimeZone(defaultTimeZone);
        }
    }, [location.search]);

    return (
        <TimeZoneContext.Provider value={timeZone}>
            {children}
        </TimeZoneContext.Provider>
    );
}

export function useTimeZone() {
    return useContext(TimeZoneContext);
}
